import React, { useState, useEffect } from 'react';
import { Car } from 'lucide-react';
import { SEO } from '../components/SEO';

interface CarImportState {
  basePrice: string;
  year: string;
  co2Emissions: string;
  engineSize: string;
  isEuro6: boolean;
  fuelType: 'petrol' | 'diesel' | 'hybrid' | 'electric';
}

interface Calculations {
  ppmv: number;
  pdv: number;
  specialTax: number;
  total: number;
}

function KalkulatorUvozaAutomobila() {
  const [values, setValues] = useState<CarImportState>({
    basePrice: '',
    year: new Date().getFullYear().toString(),
    co2Emissions: '',
    engineSize: '',
    isEuro6: true,
    fuelType: 'petrol'
  });

  const [results, setResults] = useState<Calculations | null>(null);

  const calculateDepreciation = (age: number): number => {
    if (age <= 1) return 1;
    if (age <= 2) return 0.85;
    if (age <= 3) return 0.7;
    if (age <= 4) return 0.6;
    if (age <= 5) return 0.5;
    if (age <= 6) return 0.4;
    if (age <= 7) return 0.3;
    return 0.2;
  };

  const calculatePPMV = (basePrice: number, co2: number): number => {
    let rate = 0;
    
    if (co2 <= 90) rate = 0;
    else if (co2 <= 120) rate = 0.02;
    else if (co2 <= 140) rate = 0.04;
    else if (co2 <= 160) rate = 0.07;
    else if (co2 <= 180) rate = 0.12;
    else if (co2 <= 200) rate = 0.15;
    else if (co2 <= 225) rate = 0.18;
    else if (co2 <= 250) rate = 0.21;
    else rate = 0.23;

    return basePrice * rate;
  };

  const calculateSpecialTax = (engineSize: number, isEuro6: boolean, fuelType: string): number => {
    let rate = 0;

    if (fuelType === 'diesel') {
      if (engineSize <= 1600) rate = isEuro6 ? 0.03 : 0.05;
      else if (engineSize <= 2500) rate = isEuro6 ? 0.05 : 0.07;
      else rate = isEuro6 ? 0.07 : 0.09;
    } else if (fuelType === 'petrol') {
      if (engineSize <= 1600) rate = isEuro6 ? 0.02 : 0.04;
      else if (engineSize <= 2500) rate = isEuro6 ? 0.04 : 0.06;
      else rate = isEuro6 ? 0.06 : 0.08;
    }

    return parseFloat(values.basePrice) * rate;
  };

  const calculateTaxes = () => {
    const basePrice = parseFloat(values.basePrice);
    const co2 = parseFloat(values.co2Emissions);
    const engineSize = parseFloat(values.engineSize);
    const age = new Date().getFullYear() - parseInt(values.year);

    if (isNaN(basePrice) || isNaN(co2) || isNaN(engineSize)) {
      return null;
    }

    const depreciationFactor = calculateDepreciation(age);
    const adjustedBasePrice = basePrice * depreciationFactor;

    const ppmv = calculatePPMV(adjustedBasePrice, co2);
    const pdv = adjustedBasePrice * 0.25; // 25% PDV
    const specialTax = calculateSpecialTax(engineSize, values.isEuro6, values.fuelType);

    return {
      ppmv: parseFloat(ppmv.toFixed(2)),
      pdv: parseFloat(pdv.toFixed(2)),
      specialTax: parseFloat(specialTax.toFixed(2)),
      total: parseFloat((adjustedBasePrice + ppmv + pdv + specialTax).toFixed(2))
    };
  };

  const handleInputChange = (field: keyof CarImportState, value: string | boolean) => {
    setValues(prev => ({
      ...prev,
      [field]: value
    }));
  };

  useEffect(() => {
    if (values.basePrice && values.co2Emissions && values.engineSize) {
      const results = calculateTaxes();
      setResults(results);
    }
  }, [values]);

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('hr-HR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount);
  };

  const structuredData = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    "name": "Kalkulator Uvoza Automobila",
    "applicationCategory": "CalculatorApplication",
    "operatingSystem": "Any",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "EUR"
    },
    "description": "Izračunajte troškove uvoza automobila u Hrvatsku. Uključuje izračun PDV-a, PPMV-a i posebnog poreza.",
    "url": "https://online-kalkulator.com/kalkulator-uvoza-automobila",
    "inLanguage": "hr",
    "author": {
      "@type": "Organization",
      "name": "Online Kalkulator"
    },
    "featureList": [
      "Izračun PPMV-a",
      "Izračun PDV-a",
      "Izračun posebnog poreza",
      "Automatsko ažuriranje",
      "Besplatno korištenje"
    ]
  };

  return (
    <>
      <SEO 
        title="Kalkulator Uvoza Automobila iz EU 2025 | Troškovi i PPMV"
        description="Izračunajte troškove uvoza automobila u Hrvatsku. Uključuje izračun PDV-a, PPMV-a i posebnog poreza. Precizni izračuni za 2025. godinu."
        canonicalUrl="/kalkulator-uvoza-automobila"
        structuredData={structuredData}
      />
      <div className="max-w-4xl mx-auto px-4">
        <div className="flex items-center justify-center gap-3 mb-6">
          <Car className="w-8 h-8 text-blue-600" />
          <h1 className="text-3xl font-bold text-gray-800 text-center">Kalkulator Uvoza Automobila iz EU</h1>
        </div>

        <div className="max-w-2xl mx-auto">
          {/* Calculator Section */}
          <div className="bg-white rounded-xl shadow-lg p-6 mb-8">
            <div className="space-y-6">
              {/* Input Fields */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Cijena vozila (EUR)
                  </label>
                  <input
                    type="number"
                    value={values.basePrice}
                    onChange={(e) => handleInputChange('basePrice', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 15000"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Godina proizvodnje
                  </label>
                  <input
                    type="number"
                    value={values.year}
                    onChange={(e) => handleInputChange('year', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 2020"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    CO2 emisije (g/km)
                  </label>
                  <input
                    type="number"
                    value={values.co2Emissions}
                    onChange={(e) => handleInputChange('co2Emissions', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 150"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Obujam motora (ccm)
                  </label>
                  <input
                    type="number"
                    value={values.engineSize}
                    onChange={(e) => handleInputChange('engineSize', e.target.value)}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    placeholder="npr. 1600"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Vrsta goriva
                  </label>
                  <select
                    value={values.fuelType}
                    onChange={(e) => handleInputChange('fuelType', e.target.value as CarImportState['fuelType'])}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  >
                    <option value="petrol">Benzin</option>
                    <option value="diesel">Dizel</option>
                    <option value="hybrid">Hibrid</option>
                    <option value="electric">Električni</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <label className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      checked={values.isEuro6}
                      onChange={(e) => handleInputChange('isEuro6', e.target.checked)}
                      className="w-4 h-4 text-blue-600 rounded focus:ring-blue-500"
                    />
                    <span className="text-sm text-gray-700">Euro 6 standard</span>
                  </label>
                </div>
              </div>

              {/* Results */}
              {results && (
                <div className="space-y-4 border-t pt-4">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">PPMV:</span>
                    <span className="font-semibold">{formatCurrency(results.ppmv)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">PDV (25%):</span>
                    <span className="font-semibold">{formatCurrency(results.pdv)}</span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Posebni porez:</span>
                    <span className="font-semibold">{formatCurrency(results.specialTax)}</span>
                  </div>
                  <div className="flex justify-between items-center pt-3 border-t">
                    <span className="text-gray-800 font-medium">Ukupni trošak:</span>
                    <span className="text-xl font-bold text-blue-600">{formatCurrency(results.total)}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Information Section */}
          <div className="bg-white rounded-xl shadow-lg p-8">
            <div className="space-y-8">
              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">O Kalkulatoru Uvoza Automobila</h2>
                <p className="text-gray-600 leading-relaxed">
                  Kalkulator uvoza automobila pomaže vam izračunati ukupne troškove uvoza vozila u Hrvatsku.
                  Izračun uključuje sve relevantne poreze i davanja: PDV, posebni porez na motorna vozila (PPMV)
                  i posebni porez na temelju emisije CO2 i zapremine motora.
                </p>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Što Uključuje Izračun</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>PDV (25% na osnovicu)</li>
                  <li>PPMV (posebni porez na motorna vozila)</li>
                  <li>Posebni porez na temelju emisije CO2</li>
                  <li>Korekciju vrijednosti prema starosti vozila</li>
                  <li>Dodatne faktore za dizelska i benzinska vozila</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Kako Koristiti</h2>
                <ol className="list-decimal list-inside text-gray-600 space-y-2">
                  <li>Unesite osnovnu cijenu vozila u eurima</li>
                  <li>Odaberite godinu proizvodnje vozila</li>
                  <li>Unesite emisiju CO2 u g/km</li>
                  <li>Unesite obujam motora u kubičnim centimetrima</li>
                  <li>Odaberite vrstu goriva</li>
                  <li>Označite ako vozilo zadovoljava Euro 6 standard</li>
                  <li>Rezultati će se automatski izračunati</li>
                </ol>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Važne Napomene</h2>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Izračun je informativne prirode</li>
                  <li>Stvarni troškovi mogu odstupati od izračuna</li>
                  <li>Ne uključuje troškove prijevoza i registracije</li>
                  <li>Ne uključuje troškove homologacije</li>
                  <li>Za točan izračun konzultirajte službene institucije</li>
                </ul>
              </section>

              <section>
                <h2 className="text-2xl font-semibold text-gray-700 mb-4">Dodatni Troškovi</h2>
                <p className="text-gray-600 mb-4">
                  Osim izračunatih poreza, pri uvozu automobila trebate računati i na dodatne troškove:
                </p>
                <ul className="list-disc list-inside text-gray-600 space-y-2">
                  <li>Troškovi prijevoza vozila</li>
                  <li>Troškovi homologacije</li>
                  <li>Tehnički pregled</li>
                  <li>Registracija vozila</li>
                  <li>Obvezno osiguranje</li>
                  <li>Eventualni troškovi špeditera</li>
                </ul>
              </section>
            </div>
          </div>
        </div>
      </div>
    </>
  );
}

export default KalkulatorUvozaAutomobila;